// ============================================================
// Belge ve sertifika takibi (§6)
//
// Bu modülün varlık sebebi tohum veride duran canlı bir örnek:
// lt3 Zehra Ecemiş ve lt6 Hülya Menteş'in İlk Yardım belgesi 2024
// tarihli. Bugün 2026 — belgeler süresi dolmuş durumda ve uygulama
// bunu hiçbir yerde söylemiyordu. Denetimde doğrudan ceza sebebi.
// ============================================================

import { BUGUN } from "../config.js";
import { BELGE_TURU, VARSAYILAN_TUR } from "../data/tanimlar.js";
import { B, subeBulHerYerde } from "./context.js";

export { BELGE_TURU };

// Demo belge kayıtları enjekte edilir; statik içe aktarılamaz.
//
// data/documents.js demo veri dosyası ve demo parçasına ait. Bu
// modül gerçek modda da gerekli olduğu için statik import ETSEYDİ
// —ki ediyordu— Rollup demo parçasını ana grafiğe bağlardı ve
// içindeki çocuk/personel kayıtları her ziyaretçiye inerdi.
// Gerçek kurulumda belgeler kişi kaydından gelecek; bugün boş.
let kayitliPersonelBelgeleri = {};
let kayitliSubeBelgeleri = {};

export function belgeKaynaginiAyarla({ staff = {}, branch = {} } = {}) {
  kayitliPersonelBelgeleri = staff;
  kayitliSubeBelgeleri = branch;
}

/** Uyarı eşikleri — gün. */
export const ESIK = { kritik: 7, yakin: 30, uyari: 60 };

const gunFark = (a, b) =>
  Math.round((new Date(a + "T00:00:00Z") - new Date(b + "T00:00:00Z")) / 86400000);

const turBilgisi = ad => BELGE_TURU[ad] ?? VARSAYILAN_TUR;

/**
 * "İlk Yardım (2025)" biçimindeki metni yapısal belgeye çevirir.
 * Parantez yoksa belge süresiz sayılır.
 */
export function sertifikaCoz(metin) {
  const m = /^(.*?)\s*\((\d{4})\)\s*$/.exec(metin);
  const ad = (m ? m[1] : metin).trim();
  const tur = turBilgisi(ad);
  const yil = m ? Number(m[2]) : null;

  // Veriliş günü bilinmiyor; yılın sonu alınır — en iyimser varsayım.
  // Erken tarih varsaymak belgeyi olduğundan daha erken "dolmuş"
  // gösterirdi; geç varsaymak ise gerçekte dolmuş belgeyi geçerli
  // gösterirdi. Yıl sonu, uyarıyı geciktirmeyen güvenli orta yol.
  const issuedAt = yil ? `${yil}-12-31` : null;
  const expiresAt = yil && tur.gecerlilikYil
    ? `${yil + tur.gecerlilikYil}-12-31`
    : null;

  return {
    name: ad,
    issuer: tur.veren ?? null,
    issuedAt,
    expiresAt,
    mandatory: tur.zorunlu,
    document: null,
    kaynak: "sertifika",
  };
}

/**
 * Belgenin durumu.
 *   gecerli | uyari (60g) | yakin (30g) | kritik (7g) | doldu | suresiz
 */
export function belgeDurumu(belge, bugun = BUGUN) {
  if (!belge.expiresAt) return { durum: "suresiz", kalanGun: null };

  const kalanGun = gunFark(belge.expiresAt, bugun);
  const durum =
    kalanGun < 0 ? "doldu"
    : kalanGun <= ESIK.kritik ? "kritik"
    : kalanGun <= ESIK.yakin ? "yakin"
    : kalanGun <= ESIK.uyari ? "uyari"
    : "gecerli";

  return { durum, kalanGun };
}

export const sorunluMu = belge => {
  const d = belgeDurumu(belge).durum;
  return d === "doldu" || d === "kritik" || d === "yakin" || d === "uyari";
};

export const gecersizMi = belge => belgeDurumu(belge).durum === "doldu";

// ---------- Personel ----------

/** Personelin tüm belgeleri: sertifikalar + işe alım dosyası. */
export function personelBelgeleri(kisi, branchId) {
  const tid = branchId ?? B().id;

  const sertifikalar = (kisi.certificates ?? []).map(sertifikaCoz);

  const dosyalar = (kayitliPersonelBelgeleri[tid]?.[kisi.id] ?? []).map(d => {
    const tur = turBilgisi(d.name);
    return {
      name: d.name,
      issuer: d.issuer ?? tur.veren ?? null,
      issuedAt: d.issuedAt,
      expiresAt: d.expiresAt ?? (tur.gecerlilikYil && d.issuedAt
        ? yilEkle(d.issuedAt, tur.gecerlilikYil)
        : null),
      mandatory: tur.zorunlu,
      document: d.document ?? null,
      kaynak: "dosya",
    };
  });

  return [...sertifikalar, ...dosyalar].sort(sirala);
}

function yilEkle(iso, yil) {
  const d = new Date(iso + "T00:00:00Z");
  d.setUTCFullYear(d.getUTCFullYear() + yil);
  return d.toISOString().slice(0, 10);
}

// Sorunlu belgeler üstte; süresi en yakın olan en başta.
function sirala(a, b) {
  const da = belgeDurumu(a), db = belgeDurumu(b);
  if (da.kalanGun == null && db.kalanGun == null) return a.name.localeCompare(b.name, "tr");
  if (da.kalanGun == null) return 1;
  if (db.kalanGun == null) return -1;
  return da.kalanGun - db.kalanGun;
}

/** Şubedeki tüm personelin sorunlu belgeleri. */
export function personelSorunlari(branchId) {
  const t = branchId ? subeBulHerYerde(branchId) : B();
  return t.staff
    .filter(k => k.status !== "ayrildi")
    .flatMap(k => personelBelgeleri(k, t.id)
      .filter(sorunluMu)
      .map(b => ({ kisi: k, belge: b })))
    .sort((a, b) => belgeDurumu(a.belge).kalanGun - belgeDurumu(b.belge).kalanGun);
}

// ---------- Şube ----------

export function subeBelgeleri(branchId) {
  const tid = branchId ?? B().id;
  return [...(kayitliSubeBelgeleri[tid] ?? [])]
    .map(d => ({ ...d, kaynak: "sube" }))
    .sort(sirala);
}

export const subeSorunlari = branchId => subeBelgeleri(branchId).filter(sorunluMu);

// ---------- Özet ----------

/**
 * Şubenin belge durumu. Rakamların hepsi "işlem gerekiyor" demektir.
 */
export function belgeOzeti(branchId) {
  const personel = personelSorunlari(branchId);
  const sube = subeSorunlari(branchId);
  const hepsi = [...personel.map(x => x.belge), ...sube];

  return {
    personel,
    sube,
    toplam: hepsi.length,
    dolmus: hepsi.filter(gecersizMi).length,
    zorunluDolmus: hepsi.filter(b => gecersizMi(b) && b.mandatory).length,
    yaklasan: hepsi.filter(b => !gecersizMi(b)).length,
  };
}
